import React, { useRef, useEffect, useCallback } from 'react';
import Barcode from 'react-barcode';

const isDataURL = (str) => /^data:/.test(str);
const isSVGString = (str) => /^\s*<svg/.test(str);

function detectImageMimeTypeFromBase64(base64) {
  const clean = base64?.replace(/\s/g, '');
  if (!/^[A-Za-z0-9+/=]+$/.test(clean)) return null;
  try {
    const header = atob(clean.slice(0, 24));
    const bytes = new Uint8Array([...header].map((c) => c.charCodeAt(0)));
    if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47)
      return 'image/png';
    if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return 'image/jpeg';
    if (bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x38)
      return 'image/gif';
    if (
      bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46 &&
      bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50
    )
      return 'image/webp';
  } catch (e) {
    /* ignore */
  }
  return null;
}

const SmartBarcode = ({
  data,
  onWidthChange,
  maxWidth = 200,
  barcodeProps = {},
}) => {
  const ref = useRef(null);

  const measure = useCallback(
    (w) => {
      if (typeof onWidthChange === 'function') {
        onWidthChange({ width: w, isOverflow: w > maxWidth });
      }
    },
    [onWidthChange, maxWidth]
  );

  useEffect(() => {
    if (!ref.current) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = entry.target.scrollWidth || entry.contentRect.width;
        measure(w);
      }
    });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [measure]);

  if (!data) return null;

  const wrapperStyle = { display: 'inline-block', maxWidth: '100%' };
  const imgStyle = { maxWidth: '100%', height: 50, display: 'block' };

  // ۱. Data URL
  if (isDataURL(data)) {
    return (
      <span ref={ref} style={wrapperStyle}>
        <img src={data} alt="Barcode" style={imgStyle} />
      </span>
    );
  }

  // ۲. رشته SVG
  if (isSVGString(data)) {
    const svgDataURL = `data:image/svg+xml;utf8,${encodeURIComponent(data)}`;
    return (
      <span ref={ref} style={wrapperStyle}>
        <img src={svgDataURL} alt="Barcode" style={imgStyle} />
      </span>
    );
  }

  // ۳. base64 خام
  const mimeType = detectImageMimeTypeFromBase64(data);
  if (mimeType) {
    return (
      <span ref={ref} style={wrapperStyle}>
        <img src={`data:${mimeType};base64,${data}`} alt="Barcode" style={imgStyle} />
      </span>
    );
  }

  // ۴. متن خام → react-barcode
  try {
    return (
      <span ref={ref} style={wrapperStyle}>
        <Barcode value={String(data)} {...barcodeProps} />
      </span>
    );
  } catch (err) {
    console.error('خطا در تولید بارکد:', err);
    return null;
  }
};

export default SmartBarcode;